import React, { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import html2canvas from 'html2canvas'
import Header from '../components/Header'
import { scoreAPI } from '../utils/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClockRotateLeft, faTag, faUser, faLocationDot, faStar } from '@fortawesome/free-solid-svg-icons'

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState({})
  const [history, setHistory] = useState([])
  const [chartType, setChartType] = useState('bar')
  const [breakdown, setBreakdown] = useState(false)
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(true)

  const teams = {
    pink: { name: 'สีชมพู', emoji: '🩷', color: '#ff69b4' },
    orange: { name: 'สีส้ม', emoji: '🧡', color: '#ffa500' },
    blue: { name: 'สีฟ้า', emoji: '🩵', color: '#1e90ff' },
    purple: { name: 'สีม่วง', emoji: '💜', color: '#9370db' },
    yellow: { name: 'สีเหลือง', emoji: '💛', color: '#ffd700' },
    green: { name: 'สีเขียว', emoji: '💚', color: '#32cd32' },
  }

  const createEmptyLeaderboard = () => (
    Object.fromEntries(
      Object.keys(teams).map((teamId) => [
        teamId,
        {
          total: 0,
          breakdown: {
            extra: 0,
            station: 0,
            minigame: 0,
            others: 0,
          },
          lastUpdated: null,
        },
      ])
    )
  )

  const normalizeScoreRecords = (scoresRoot) => {
    const normalized = createEmptyLeaderboard()

    Object.entries(scoresRoot || {}).forEach(([teamId, teamScores]) => {
      if (!teams[teamId] || !teamScores || typeof teamScores !== 'object') {
        return
      }

      Object.values(teamScores).forEach((scoreRecord) => {
        if (!scoreRecord || typeof scoreRecord !== 'object') {
          return
        }

        const amount = Number(scoreRecord.amount)
        const scoreType = scoreRecord.scoreType

        if (!Number.isFinite(amount) || !scoreType || !normalized[teamId].breakdown.hasOwnProperty(scoreType)) {
          return
        }

        normalized[teamId].total += amount
        normalized[teamId].breakdown[scoreType] += amount

        const recordTime = scoreRecord.createdAt ? new Date(scoreRecord.createdAt).getTime() : 0
        const currentTime = normalized[teamId].lastUpdated ? new Date(normalized[teamId].lastUpdated).getTime() : 0

        if (!normalized[teamId].lastUpdated || recordTime > currentTime) {
          normalized[teamId].lastUpdated = scoreRecord.createdAt || normalized[teamId].lastUpdated
        }
      })
    })

    return normalized
  }

  const typeStyles = {
    extra: 'bg-blue-100 text-blue-800',
    station: 'bg-purple-100 text-purple-800',
    minigame: 'bg-pink-100 text-pink-800',
    others: 'bg-amber-100 text-amber-800',
  }

  const typeLabels = {
    extra: 'Extra',
    station: 'Station',
    minigame: 'Minigame',
    others: 'Others',
  }

  const loadLeaderboard = async (cancelledRef) => {
    try {
      const response = await scoreAPI.getLeaderboard()
      if (!cancelledRef.cancelled) {
        setLeaderboard(response.data || createEmptyLeaderboard())
        setLoading(false)
      }
    } catch (error) {
      if (!cancelledRef.cancelled) {
        setLeaderboard(createEmptyLeaderboard())
        setLoading(false)
      }
    }
  }

  const loadHistory = async (cancelledRef) => {
    try {
      const response = await scoreAPI.getAllHistory()
      if (!cancelledRef.cancelled) {
        setHistory(response.data || [])
        setHistoryLoading(false)
      }
    } catch (error) {
      if (!cancelledRef.cancelled) {
        setHistory([])
        setHistoryLoading(false)
      }
    }
  }

  useEffect(() => {
    const cancelledRef = { cancelled: false }

    loadLeaderboard(cancelledRef)
    loadHistory(cancelledRef)
    const intervalId = setInterval(() => {
      loadLeaderboard(cancelledRef)
      loadHistory(cancelledRef)
    }, 5000)

    return () => {
      cancelledRef.cancelled = true
      clearInterval(intervalId)
    }
  }, [])

  const getChartData = () => {
    return Object.entries(teams).map(([teamId, team]) => ({
      name: `${team.emoji} ${team.name}`,
      total: leaderboard[teamId]?.total || 0,
      extra: leaderboard[teamId]?.breakdown?.extra || 0,
      station: leaderboard[teamId]?.breakdown?.station || 0,
      minigame: leaderboard[teamId]?.breakdown?.minigame || 0,
      others: leaderboard[teamId]?.breakdown?.others || 0,
    }))
  }

  const sortedTeams = getChartData().sort((a, b) => b.total - a.total)
  const recentHistory = [...history].slice(0, 30)

  const handleExportImage = async () => {
    const element = document.getElementById('leaderboard-chart')
    if (element) {
      try {
        const canvas = await html2canvas(element)
        const link = document.createElement('a')
        link.href = canvas.toDataURL('image/png')
        link.download = `leaderboard-${new Date().toISOString().split('T')[0]}.png`
        link.click()
      } catch (error) {
        console.error('Error exporting image:', error)
      }
    }
  }

  return (
    <div>
      <Header user={null} />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h1 className="text-4xl font-bold">🏆 Leaderboard</h1>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setChartType('bar')}
              className={`btn btn-sm ${chartType === 'bar' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Bar Chart
            </button>
            <button
              onClick={() => setChartType('pie')}
              className={`btn btn-sm ${chartType === 'pie' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Pie Chart
            </button>
            <button
              onClick={() => setBreakdown(!breakdown)}
              className={`btn btn-sm ${breakdown ? 'btn-primary' : 'btn-secondary'}`}
            >
              {breakdown ? 'Total' : 'Breakdown'}
            </button>
            <button
              onClick={handleExportImage}
              className="btn btn-sm btn-secondary"
            >
              📸 Export
            </button>
          </div>
        </div>

        <div id="leaderboard-chart" className="bg-white p-6 rounded-lg shadow-lg mb-8">
          {loading ? (
            <div className="h-[400px] flex items-center justify-center text-gray-500">
              Loading leaderboard...
            </div>
          ) : chartType === 'bar' ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={sortedTeams}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {breakdown ? (
                  <>
                    <Bar dataKey="extra" stackId="a" fill="#3b82f6" name="Extra" />
                    <Bar dataKey="station" stackId="a" fill="#8b5cf6" name="Station" />
                    <Bar dataKey="minigame" stackId="a" fill="#ec4899" name="Minigame" />
                    <Bar dataKey="others" stackId="a" fill="#f59e0b" name="Others" />
                  </>
                ) : (
                  <Bar dataKey="total" fill="#3b82f6" name="Total Points" />
                )}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={sortedTeams}
                  dataKey="total"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label
                >
                  {sortedTeams.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(teams)[index].color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card mb-8 border border-slate-200">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl font-black inline-flex items-center gap-2">
                <FontAwesomeIcon icon={faClockRotateLeft} /> Score History
              </h2>
              <p className="text-gray-600 mt-1">Recent score logs from all teams, color-coded by team and score type.</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">
              {history.length} logs
            </span>
          </div>

          {historyLoading ? (
            <div className="py-8 text-center text-gray-500">Loading score history...</div>
          ) : recentHistory.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No score history yet.</div>
          ) : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {recentHistory.map((entry) => {
                const team = teams[entry.teamId] || { name: entry.teamId, emoji: '🏁', color: '#64748b' }
                return (
                  <div key={entry.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div
                        className="h-12 w-12 rounded-2xl text-white flex items-center justify-center font-black shrink-0"
                        style={{ backgroundColor: team.color }}
                      >
                        {team.emoji}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-black text-slate-900">{team.name}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${typeStyles[entry.scoreType] || 'bg-slate-100 text-slate-700'}`}>
                            <FontAwesomeIcon icon={faTag} className="mr-1" />
                            {typeLabels[entry.scoreType] || entry.scoreType}
                          </span>
                          {entry.stationId && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 inline-flex items-center gap-1">
                              <FontAwesomeIcon icon={faLocationDot} />
                              {entry.stationId}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 flex flex-wrap gap-2 items-center">
                          <span className="inline-flex items-center gap-1"><FontAwesomeIcon icon={faUser} /> {entry.staffName}</span>
                          {entry.note && <span className="text-gray-400">• {entry.note}</span>}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 md:text-right">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500">Points</p>
                        <p className="text-2xl font-black text-slate-900 inline-flex items-center gap-2">
                          <FontAwesomeIcon icon={faStar} className="text-amber-500" />
                          {entry.amount}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500">Time</p>
                        <p className="text-sm font-semibold text-slate-700">
                          {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Rankings Table */}
        <div className="card">
          <h2 className="text-2xl font-bold mb-4">Rankings</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b-2">
                <tr>
                  <th className="text-left px-4 py-2">#</th>
                  <th className="text-left px-4 py-2">Team</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-right px-4 py-2">Extra</th>
                  <th className="text-right px-4 py-2">Station</th>
                  <th className="text-right px-4 py-2">Minigame</th>
                  <th className="text-right px-4 py-2">Others</th>
                </tr>
              </thead>
              <tbody>
                {sortedTeams.map((team, index) => (
                  <tr key={team.name} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-bold">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                    </td>
                    <td className="px-4 py-3 font-semibold">{team.name}</td>
                    <td className="px-4 py-3 text-right font-bold text-lg">{team.total}</td>
                    <td className="px-4 py-3 text-right">{team.extra}</td>
                    <td className="px-4 py-3 text-right">{team.station}</td>
                    <td className="px-4 py-3 text-right">{team.minigame}</td>
                    <td className="px-4 py-3 text-right">{team.others}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          {leaderboard[Object.keys(teams)[0]]?.lastUpdated && (
            <p>
              Last updated: {new Date(leaderboard[Object.keys(teams)[0]].lastUpdated).toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default Leaderboard
